import { typeValidator } from '@chilipiper/utils'
import React, { Children, useMemo } from 'react'
import { Key } from 'react-aria'
import invariant from 'tiny-invariant'
import { DynamicItem, selectItemPublicToDynamicItem } from '../base-select/dynamicItemUtils'
import { ItemData } from '../base-select/itemData.types'
import {
  CascadeSelectItemPublicComponent,
  SelectItemPublicComponent,
  SelectSectionPublicComponent,
} from '../base-select/publicComponentTypes'
import {
  CascadeSelectItem,
  SelectEndContent,
  SelectItem,
  SelectSection,
  SelectStartContent,
} from '../select/PublicComponents'
import { SelectItemPlaceholder } from '../select-item/SelectItemPlaceholder'

type ListProps = {
  children: React.ReactNode
  selectedItem?: React.ReactNode
  selectedItems?: React.ReactNode[]
  componentName?: string
  search?: string
}

type AddOptions = {
  notLoadedSelectedItem?: boolean
}

export const isVisible = (search?: string, textValue?: string) =>
  !search || (search && textValue?.toLocaleLowerCase().includes(search.toLocaleLowerCase()))

export const useSelectListData = ({
  children,
  componentName = 'Select',
  search,
  selectedItem,
  selectedItems,
}: ListProps) => {
  return useMemo(() => {
    const childrenArray = React.Children.toArray(children)
    const disabledKeys: string[] = []
    const existingValues: Set<string> = new Set()
    const itemData: Record<Key, ItemData> = {}
    const options: Array<DynamicItem> = []

    const registerItemAndGetDynamicData = (
      item: SelectItemPublicComponent,
      addOptions?: AddOptions
    ) => {
      const { endContent, isDisabled, startContent, value: optionValue } = item.props

      invariant(
        endContent === undefined || typeValidator(SelectEndContent)(endContent),
        `endContent must be a ${componentName}.EndContent component or undefined`
      )

      invariant(
        startContent === undefined || typeValidator(SelectStartContent)(startContent),
        `startContent must be a ${componentName}.StartContent component or undefined`
      )

      existingValues.add(optionValue)

      const dynamicItemProps = selectItemPublicToDynamicItem(item, 'item')
      const isHidden =
        addOptions?.notLoadedSelectedItem || !isVisible(search, dynamicItemProps.textValue)
      itemData[optionValue] = {
        ...dynamicItemProps,
        textValue: item.props.textValue ?? item.props.children,
        isHidden,
      }

      if (isDisabled || isHidden) {
        disabledKeys.push(optionValue)
      }

      return dynamicItemProps
    }
    const registerCascadeItemAndGetDynamicData = (item: CascadeSelectItemPublicComponent) => {
      const { endContent, isDisabled, value: optionValue } = item.props

      invariant(
        endContent === undefined || typeValidator(SelectEndContent)(endContent),
        `endContent must be a ${componentName}.EndContent component or undefined`
      )

      existingValues.add(optionValue)

      const dynamicItemProps = selectItemPublicToDynamicItem(item, 'cascade')
      const isHidden = !isVisible(search, dynamicItemProps.label)
      itemData[optionValue] = {
        textValue: '',
        ...dynamicItemProps,
        isHidden,
      }
      // TODO handle search correctly...
      if (isDisabled || isHidden) {
        disabledKeys.push(optionValue)
      }

      return dynamicItemProps
    }
    const addSection = (section: SelectSectionPublicComponent) => {
      const sectionItems = Children.toArray(section.props.children)

      const sectionChildren = sectionItems.map(sectionItem => {
        invariant(
          typeValidator(SelectItem)(sectionItem) || typeValidator(CascadeSelectItem)(sectionItem),
          'All SelectList.Section children must be SelectList.Item or SelectList.CascadeItem'
        )

        if (sectionItem.type === CascadeSelectItem) {
          return registerCascadeItemAndGetDynamicData(
            sectionItem as CascadeSelectItemPublicComponent
          )
        } else {
          return registerItemAndGetDynamicData(sectionItem as SelectItemPublicComponent)
        }
      })

      options.push(
        selectItemPublicToDynamicItem(
          { title: section.props.title, children: sectionChildren },
          'section'
        )
      )
    }
    const addItem = (item: SelectItemPublicComponent, addOptions?: AddOptions) =>
      options.push(registerItemAndGetDynamicData(item, addOptions))
    const addCascadeItem = (item: CascadeSelectItemPublicComponent) =>
      options.push(registerCascadeItemAndGetDynamicData(item))

    for (const child of childrenArray) {
      if (typeValidator(SelectSection)(child)) {
        addSection(child)
      } else if (typeValidator(CascadeSelectItem)(child)) {
        addCascadeItem(child)
      } else if (typeValidator(SelectItem)(child)) {
        addItem(child)
      } else if (typeValidator(SelectItemPlaceholder)(child)) {
        addItem(
          React.cloneElement(child as unknown as SelectItemPublicComponent, {
            value: `INTERNAL_placeholder-${options.length}`,
            textValue: search || 'Loading', // use search to make sure placeholder is not filtered out when doing FE search
            children: child,
            isDisabled: true,
          })
        )
      } else {
        throw new Error(
          `All ${componentName} children must be ${componentName}.Item or ${componentName}.Section, ${componentName}.CascadeItem or ${componentName}.ItemPlaceholder`
        )
      }
    }

    const mergedSelectedItems: React.ReactNode[] = []

    if (selectedItem) {
      mergedSelectedItems.push(selectedItem)
    }

    if (selectedItems) {
      mergedSelectedItems.push(...selectedItems)
    }

    if (mergedSelectedItems.every(selectedItem => typeValidator(SelectItem)(selectedItem))) {
      mergedSelectedItems.forEach(selectedItem => {
        if (!existingValues.has(selectedItem.props.value)) {
          addItem(selectedItem, { notLoadedSelectedItem: true })
        }
      })
    } else {
      throw new Error(`selectedItem must be a ${componentName}.Item component or undefined`)
    }

    return {
      disabledKeys,
      existingValues,
      itemData,
      options,
    }
  }, [children, componentName, search, selectedItem, selectedItems])
}
